import React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Pagination,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';

import { getRequest } from 'Includes/api/apicall';
import { GET_URL } from 'Includes/urls';
import { loadBillingFilters, saveBillingFilters } from 'Includes/billingFilterStorage';
import OrdersSourceToggle, { loadOrdersSource } from 'Components/OrdersSourceToggle';
import { TEAM_STATUS_CHOICES } from '../../Includes/teamStatus';
import ReportsMobileBottomNav from 'Components/ReportsMobileBottomNav';

function ServiceWiseBillingSummaryList(props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const storedFilters = loadBillingFilters();

  const [academicYearList, setAcademicYearList] = React.useState([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = React.useState(storedFilters.academicYearId || '');
  const [statusByTeamFilter, setStatusByTeamFilter] = React.useState('all');
  const [clientBillingStatusFilter, setClientBillingStatusFilter] = React.useState('');
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const [sortModel] = React.useState([
    { field: 'balance_to_be_paid_by_today', sort: 'desc' }
  ]);

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = React.useState(0);
  const [overallSummaryData, setOverallSummaryData] = React.useState(null);
  const [orderSource, setOrderSource] = React.useState(loadOrdersSource());

  const formatAmount = (v) => {
    const n = parseFloat(v || 0);
    return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const overallSummary = React.useMemo(() => {
    if (!overallSummaryData) {
      return {
        totals: { order: 0, issued: 0, invoiced: 0, paid: 0, balance: 0, discount: 0, dueByToday: 0, balanceToPayByToday: 0 }
      };
    }
    return overallSummaryData;
  }, [overallSummaryData]);

  React.useEffect(() => {
    getAcademicYearList();
  }, []);

  const getAcademicYearList = () => {
    getRequest(GET_URL.academicyear.api, {}, props).then((res) => {
      if (res?.status === 200) {
        const list = res.data.data || [];
        setAcademicYearList(list);

        if (selectedAcademicYear) {
          fetchSummary(selectedAcademicYear);
        }
      }
    });
  };

  const fetchSummary = (yearId) => {
    if (!yearId) return;

    setLoading(true);

    const params = {
      year: yearId,
      limit: paginationModel.pageSize,
      pageno: paginationModel.page + 1,
      ...(statusByTeamFilter && { status_by_team: statusByTeamFilter }),
      ...(clientBillingStatusFilter && { client_billing_status: clientBillingStatusFilter }),
      order_source: orderSource,
      ...(sortModel?.length && {
        ordering: sortModel[0].sort === 'desc'
          ? `-${sortModel[0].field}`
          : sortModel[0].field
      })
    };

    getRequest(GET_URL.billing_config.api, { ...params, service_wise_finance_summary: 1 }, props)
      .then((res) => {
        setLoading(false);
        if (res?.status === 200) {
          setRows(res.data?.data || []);
          setRowCount(res.data?.count || 0);
          setOverallSummaryData(res.data?.summary || null);
        }
      })
      .catch(() => setLoading(false));
  };

  React.useEffect(() => {
    if (selectedAcademicYear) {
      fetchSummary(selectedAcademicYear);
    }
  }, [statusByTeamFilter, clientBillingStatusFilter, sortModel, paginationModel, selectedAcademicYear, orderSource]);

  const columns = [
    {
      field: 'service_name',
      headerName: 'Service',
      minWidth: 200,
      flex: 1,
      renderCell: (params) => {
        const balance = parseFloat(params.row.balance_amount || 0);
        const due = parseFloat(params.row.balance_to_be_paid_by_today || 0);
        const color = due > 0 ? '#d32f2f' : balance <= 0 ? '#2e7d32' : '#0f172a';

        return (
          <Typography fontWeight={700} color={color}>
            {params.value || '-'}
          </Typography>
        );
      }
    },
    {
      field: 'order_total_amount',
      headerName: 'Order Total',
      width: 160,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600} color="#1e293b">
          {formatAmount(params.value)}
        </Typography>
      )
    },
    {
      field: 'issued_invoice_total_amount',
      headerName: 'Issued Invoice',
      width: 160,
      renderCell: (params) => (
        <Typography variant="body2" color="#475569">
          {formatAmount(params.value)}
        </Typography>
      )
    },
    {
      field: 'paid_total_amount',
      headerName: 'Paid',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={700} color="#16a34a">
          {formatAmount(params.value)}
        </Typography>
      )
    },
    {
      field: 'balance_amount',
      headerName: 'Balance',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600} color="#ea580c">
          {formatAmount(params.value)}
        </Typography>
      )
    },
    {
      field: 'balance_to_be_paid_by_today',
      headerName: 'Due Today',
      width: 160,
      renderCell: (params) => {
        const val = parseFloat(params.value || 0);
        return (
          <Typography
            variant="body2"
            fontWeight={val > 0 ? 800 : 500}
            color={val > 0 ? '#dc2626' : '#64748b'}
          >
            {formatAmount(val)}
          </Typography>
        );
      }
    },
    {
      field: 'no_of_companies',
      headerName: 'Companies',
      width: 130,
      renderCell: (params) => (
        <Chip
          size="small"
          label={`${params.value || 0} clients`}
          sx={{ fontWeight: 600, fontSize: '0.75rem', bgcolor: '#f1f5f9', color: '#334155' }}
        />
      )
    },
  ];

  const orderTotal = parseFloat(overallSummary.totals.order || 0);
  const paidTotal = parseFloat(overallSummary.totals.paid || 0);
  const balanceTotal = parseFloat(overallSummary.totals.balance || 0);
  const dueTotal = parseFloat(overallSummary.totals.balanceToPayByToday || 0);
  const overallPaidPercent = orderTotal ? `${((paidTotal / orderTotal) * 100).toFixed(1)}%` : '0.0%';

  return (
    <Box p={{ xs: 1.5, sm: 2.5 }} sx={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>

      {/* HEADER */}
      <Box mb={2}>
        <Grid container alignItems="center" justifyContent="space-between" spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={800} color="#0f172a" sx={{ letterSpacing: '-0.02em' }}>
              Service Wise Summary
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Revenue, collections, and pending balances grouped by service
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, alignItems: 'center' }}>
            <OrdersSourceToggle
              value={orderSource}
              onChange={(next) => {
                setOrderSource(next);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* KPI SUMMARY CARDS */}
      {selectedAcademicYear && (
        <Grid container spacing={1.5} mb={2}>
          {/* Services Count */}
          <Grid item xs={6} sm={4} md={2.4}>
            <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2.5, border: '1px solid #e2e8f0', backgroundColor: '#ffffff' }}>
              <Box display="flex" alignItems="center" gap={0.8} mb={0.3}>
                <BusinessOutlinedIcon sx={{ fontSize: 16, color: '#64748b' }} />
                <Typography variant="caption" fontWeight={700} color="#64748b" textTransform="uppercase" fontSize="0.65rem">
                  Services
                </Typography>
              </Box>
              <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={800} color="#0f172a">
                {rowCount}
              </Typography>
            </Paper>
          </Grid>

          {/* Order Total */}
          <Grid item xs={6} sm={4} md={2.4}>
            <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2.5, border: '1px solid #e2e8f0', backgroundColor: '#ffffff' }}>
              <Box display="flex" alignItems="center" gap={0.8} mb={0.3}>
                <ReceiptLongOutlinedIcon sx={{ fontSize: 16, color: '#3b82f6' }} />
                <Typography variant="caption" fontWeight={700} color="#3b82f6" textTransform="uppercase" fontSize="0.65rem">
                  Orders
                </Typography>
              </Box>
              <Typography variant="subtitle1" fontWeight={800} color="#0f172a" noWrap>
                {formatAmount(orderTotal)}
              </Typography>
            </Paper>
          </Grid>

          {/* Paid */}
          <Grid item xs={6} sm={4} md={2.4}>
            <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2.5, border: '1px solid #bbf7d0', backgroundColor: '#f0fdf4' }}>
              <Box display="flex" alignItems="center" gap={0.8} mb={0.3}>
                <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#16a34a' }} />
                <Typography variant="caption" fontWeight={700} color="#16a34a" textTransform="uppercase" fontSize="0.65rem">
                  Paid ({overallPaidPercent})
                </Typography>
              </Box>
              <Typography variant="subtitle1" fontWeight={800} color="#15803d" noWrap>
                {formatAmount(paidTotal)}
              </Typography>
            </Paper>
          </Grid>

          {/* Balance */}
          <Grid item xs={6} sm={4} md={2.4}>
            <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2.5, border: '1px solid #fed7aa', backgroundColor: '#fff7ed' }}>
              <Box display="flex" alignItems="center" gap={0.8} mb={0.3}>
                <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 16, color: '#ea580c' }} />
                <Typography variant="caption" fontWeight={700} color="#ea580c" textTransform="uppercase" fontSize="0.65rem">
                  Balance
                </Typography>
              </Box>
              <Typography variant="subtitle1" fontWeight={800} color="#c2410c" noWrap>
                {formatAmount(balanceTotal)}
              </Typography>
            </Paper>
          </Grid>

          {/* Due Today */}
          <Grid item xs={12} sm={4} md={2.4}>
            <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2.5, border: dueTotal > 0 ? '1.5px solid #fecaca' : '1px solid #e2e8f0', backgroundColor: dueTotal > 0 ? '#fef2f2' : '#ffffff' }}>
              <Box display="flex" alignItems="center" gap={0.8} mb={0.3}>
                <ErrorOutlineOutlinedIcon sx={{ fontSize: 16, color: dueTotal > 0 ? '#dc2626' : '#64748b' }} />
                <Typography variant="caption" fontWeight={700} color={dueTotal > 0 ? '#dc2626' : '#64748b'} textTransform="uppercase" fontSize="0.65rem">
                  Due Today
                </Typography>
              </Box>
              <Typography variant="subtitle1" fontWeight={800} color={dueTotal > 0 ? '#b91c1c' : '#0f172a'} noWrap>
                {formatAmount(dueTotal)}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* STICKY FILTERS */}
      <Paper
        elevation={0}
        sx={{
          position: 'sticky',
          top: { xs: 66, sm: 66 },
          zIndex: 90,
          p: 1.5,
          mb: 2,
          borderRadius: 2.5,
          border: '1px solid #e2e8f0',
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
        }}
      >
        <Grid container spacing={1.5}>
          <Grid item md={4} xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Academic Year</InputLabel>
              <Select
                value={selectedAcademicYear}
                onChange={(e) => {
                  setSelectedAcademicYear(e.target.value);
                  saveBillingFilters({ academicYearId: e.target.value });
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
                label="Academic Year"
                sx={{ borderRadius: 2 }}
              >
                {academicYearList.map((y) => (
                  <MenuItem key={y.id} value={y.id}>{y.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item md={4} xs={6} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status (Team)</InputLabel>
              <Select
                value={statusByTeamFilter}
                label="Status (Team)"
                onChange={(e) => {
                  setStatusByTeamFilter(e.target.value);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
                sx={{ borderRadius: 2 }}
              >
                {TEAM_STATUS_CHOICES.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item md={4} xs={6} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Billing Status</InputLabel>
              <Select
                value={clientBillingStatusFilter}
                label="Billing Status"
                onChange={(e) => {
                  setClientBillingStatusFilter(e.target.value);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="GOOD">GOOD</MenuItem>
                <MenuItem value="CRITICAL">CRITICAL</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* CONTENT: MOBILE CARDS VS DESKTOP TABLE */}
      {isMobile ? (
        <Box>
          {loading ? (
            <Box textAlign="center" py={4}>
              <CircularProgress size={32} />
            </Box>
          ) : rows.length === 0 ? (
            <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 3, border: '1px solid #e2e8f0', backgroundColor: '#ffffff' }}>
              <Typography color="text.secondary">No service records found</Typography>
            </Paper>
          ) : (
            rows.map((row) => {
              const due = parseFloat(row.balance_to_be_paid_by_today || 0);
              const order = parseFloat(row.order_total_amount || 0);
              const paid = parseFloat(row.paid_total_amount || 0);
              const balance = parseFloat(row.balance_amount || 0);

              return (
                <Paper
                  key={row.id || row.service_id || row.service_name}
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 1.5,
                    borderRadius: 2.5,
                    border: due > 0 ? '1.5px solid #fecaca' : '1px solid #e2e8f0',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body1" fontWeight={800} color="#0f172a">
                      {row.service_name || 'Unknown Service'}
                    </Typography>
                    <Chip
                      size="small"
                      label={`${row.no_of_companies || 0} clients`}
                      sx={{ fontWeight: 700, fontSize: '0.72rem', bgcolor: '#f1f5f9', color: '#334155' }}
                    />
                  </Box>

                  {due > 0 && (
                    <Box
                      sx={{
                        mb: 1,
                        p: 1,
                        borderRadius: 2,
                        backgroundColor: '#fef2f2',
                        border: '1px solid #fee2e2',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant="caption" fontWeight={700} color="#dc2626">
                        ⚠️ Due Today
                      </Typography>
                      <Typography variant="body2" fontWeight={800} color="#b91c1c">
                        {formatAmount(due)}
                      </Typography>
                    </Box>
                  )}

                  <Box
                    sx={{
                      p: 1.2,
                      borderRadius: 2,
                      backgroundColor: '#f8fafc',
                      border: '1px solid #f1f5f9',
                    }}
                  >
                    <Grid container spacing={1}>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="#64748b" display="block" fontSize="0.7rem">
                          Order Total
                        </Typography>
                        <Typography variant="body2" fontWeight={700} color="#1e293b" noWrap>
                          {formatAmount(order)}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="#16a34a" display="block" fontSize="0.7rem">
                          Collected
                        </Typography>
                        <Typography variant="body2" fontWeight={700} color="#15803d" noWrap>
                          {formatAmount(paid)}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="#ea580c" display="block" fontSize="0.7rem">
                          Balance
                        </Typography>
                        <Typography variant="body2" fontWeight={700} color="#c2410c" noWrap>
                          {formatAmount(balance)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  <Box display="flex" justifyContent="space-between" alignItems="center" mt={1.2} pt={0.8} borderTop="1px solid #f1f5f9">
                    <Typography variant="caption" color="text.secondary">
                      Invoiced: {formatAmount(row.issued_invoice_total_amount)}
                    </Typography>
                  </Box>
                </Paper>
              );
            })
          )}

          {rowCount > paginationModel.pageSize && (
            <Box display="flex" justifyContent="center" alignItems="center" py={2}>
              <Pagination
                count={Math.ceil(rowCount / paginationModel.pageSize)}
                page={paginationModel.page + 1}
                onChange={(e, p) => setPaginationModel((prev) => ({ ...prev, page: p - 1 }))}
                color="primary"
                size="small"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Box>
      ) : (
        <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', backgroundColor: '#ffffff', overflow: 'hidden' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => String(row.id || row.service_id || row.service_name)}
            loading={loading}
            autoHeight
            rowHeight={52}
            headerHeight={46}
            pagination
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 20, 50]}
            rowCount={rowCount}
            disableSelectionOnClick
            sx={{
              border: 0,
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f8fafc',
                color: '#475569',
                fontWeight: 700,
                fontSize: '0.82rem',
                borderBottom: '1px solid #e2e8f0',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #f1f5f9',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#f8fafc',
              },
            }}
          />
        </Paper>
      )}

      {/* Mobile Bottom Navigation for 1-tap switching between the 3 reports */}
      <ReportsMobileBottomNav />
    </Box>
  );
}

export default ServiceWiseBillingSummaryList;